// Venn diagram (Year 8, wording approved by the owner, 2 Nov 2026). A
// `venn` is a box, with an optional label above it ("40 people"), holding
// two overlapping circles with their names ("Coffee", "Cake"). Each of the
// four parts shows a number, a short list of things, or nothing:
// `firstOnly` (in the first circle only), `both`, `secondOnly` and
// `outside` (in the box, outside both circles).
//
// A part the child must find is left blank. The picture never holds an
// answer box or a "?" (answer boxes go in the question words).
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type VennRegion = 'both' | 'firstOnly' | 'secondOnly' | 'outside';

export type VennSpec = {
  type: 'venn';
  // Above the box; "" for none.
  label: string;
  circles: [string, string];
  // What each part shows, as lines of words; none for a blank part.
  regions: Record<VennRegion, string[]>;
};

export const VENN_REGIONS: VennRegion[] = [
  'both',
  'firstOnly',
  'secondOnly',
  'outside',
];

const MAX = { label: 24, name: 14, item: 8, items: 12 };

function readText(value: unknown, longest: number): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text === '' || text.length > longest) return null;
  // Never an answer box or a question mark.
  if (/[[\]?]/.test(text)) return null;
  return text;
}

function readItem(value: unknown): string | null {
  if (typeof value === 'number')
    return Number.isFinite(value) && value >= 0 ? String(value) : null;
  return readText(value, MAX.item);
}

// A part's things: a number, one word, or a list; none when blank.
function readRegion(value: unknown): string[] | null {
  if (value === undefined || value === '') return [];
  if (Array.isArray(value)) {
    if (value.length > MAX.items) return null;
    const items: string[] = [];
    for (const entry of value as unknown[]) {
      const item = readItem(entry);
      if (item === null) return null;
      items.push(item);
    }
    return items;
  }
  const item = readItem(value);
  return item === null ? null : [item];
}

export function readVenn(record: Record<string, unknown>): VennSpec | null {
  const label =
    record.label === undefined ? '' : readText(record.label, MAX.label);
  if (label === null) return null;
  const { circles } = record;
  if (!Array.isArray(circles) || circles.length !== 2) return null;
  const first = readText(circles[0], MAX.name);
  const second = readText(circles[1], MAX.name);
  if (first === null || second === null || first === second) return null;
  const regions = {} as Record<VennRegion, string[]>;
  for (const region of VENN_REGIONS) {
    const items = readRegion(record[region]);
    if (items === null) return null;
    regions[region] = items;
  }
  const venn: VennSpec = {
    type: 'venn',
    label,
    circles: [first, second],
    regions,
  };
  // Every part's things must fit inside it.
  return vennPicture(venn) ? venn : null;
}

// "9", "2 and 4", "2, 4 and 8".
function spokenItems(items: string[]): string {
  if (items.length === 0) return 'blank';
  if (items.length === 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;
}

export function describeVenn(venn: VennSpec): string {
  const [first, second] = venn.circles;
  const { regions } = venn;
  return [
    venn.label === ''
      ? `A box holding two overlapping circles, ${first} and ${second}.`
      : `A box labelled ${venn.label} holding two overlapping circles, ${first} and ${second}.`,
    `In both circles: ${spokenItems(regions.both)}.`,
    `In ${first} only: ${spokenItems(regions.firstOnly)}.`,
    `In ${second} only: ${spokenItems(regions.secondOnly)}.`,
    `Outside both circles: ${spokenItems(regions.outside)}.`,
  ].join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn, in units close to one pixel each at the
// picture's natural width. The middle of the overlap is at 0, 0.

export type VennText = {
  x: number;
  y: number;
  text: string;
  anchor: 'start' | 'middle' | 'end';
  part: VennRegion | 'name' | 'label';
};

export type VennPicture = {
  radius: number;
  centres: [number, number];
  frame: { x: number; y: number; width: number; height: number };
  texts: VennText[];
  box: { x: number; y: number; width: number; height: number };
};

const RADIUS = 80;
// From the middle to each circle's centre.
const APART = 46;
export const VENN_TEXT_SIZE = 14;
const LETTER_WIDTH = 8.4;
const TEXT_HEIGHT = 15;
const LINE = 17;
// Room kept clear inside a part, on each side of its words.
const MARGIN = 4;
const FRAME = { side: 22, top: 34, bottom: 32 };
const PAD = 6;
const MOST_LINES: Record<VennRegion, number> = {
  both: 4,
  firstOnly: 5,
  secondOnly: 5,
  outside: 1,
};

function widthOf(text: string): number {
  return text.length * LETTER_WIDTH;
}

// Half the width of a circle at a height y from its centre.
function half(y: number): number {
  return Math.sqrt(Math.max(0, RADIUS * RADIUS - y * y));
}

// The room across a part between the heights top and bottom (top < bottom),
// as its left and right edges.
function room(region: VennRegion, top: number, bottom: number) {
  const nearest =
    top <= 0 && bottom >= 0 ? 0 : Math.min(Math.abs(top), Math.abs(bottom));
  const furthest = Math.max(Math.abs(top), Math.abs(bottom));
  const wide = half(nearest);
  const narrow = half(furthest);
  if (region === 'both') return { left: APART - narrow, right: narrow - APART };
  if (region === 'firstOnly')
    return { left: -APART - narrow, right: APART - wide };
  return { left: wide - APART, right: APART + narrow };
}

// Items joined by commas, broken into lines no wider than the part.
function lines(items: string[], fits: (text: string) => boolean) {
  const out: string[] = [];
  let line = '';
  items.forEach((item, index) => {
    const piece = index < items.length - 1 ? `${item},` : item;
    const longer = line === '' ? piece : `${line} ${piece}`;
    if (line !== '' && !fits(longer)) {
      out.push(line);
      line = piece;
    } else line = longer;
  });
  if (line !== '') out.push(line);
  return out;
}

// Null when a part's things do not fit inside it.
export function vennPicture(venn: VennSpec): VennPicture | null {
  const right = APART + RADIUS + FRAME.side;
  const frame = {
    x: -right,
    y: -RADIUS - FRAME.top,
    width: right * 2,
    height: RADIUS * 2 + FRAME.top + FRAME.bottom,
  };
  const texts: VennText[] = [];
  // Each circle's name above it, a little out from its centre so long
  // names stay apart.
  const nameY = -RADIUS - 14;
  venn.circles.forEach((name, index) => {
    const way = index === 0 ? -1 : 1;
    const x = way * (APART + 30);
    texts.push({ x, y: nameY, text: name, anchor: 'middle', part: 'name' });
  });
  if (venn.label !== '')
    texts.push({
      x: frame.x,
      y: frame.y - 12,
      text: venn.label,
      anchor: 'start',
      part: 'label',
    });
  for (const region of ['both', 'firstOnly', 'secondOnly'] as const) {
    const items = venn.regions[region];
    if (items.length === 0) continue;
    // The widest the lines may be: try the most lines this part takes.
    const most = MOST_LINES[region];
    const extent = ((most - 1) * LINE) / 2 + TEXT_HEIGHT / 2;
    const widest = room(region, -extent, extent);
    const split = lines(
      items,
      (text) => widthOf(text) + 2 * MARGIN <= widest.right - widest.left,
    );
    if (split.length > most) return null;
    for (const [index, text] of split.entries()) {
      const y = (index - (split.length - 1) / 2) * LINE;
      const edges = room(region, y - TEXT_HEIGHT / 2, y + TEXT_HEIGHT / 2);
      if (widthOf(text) + 2 * MARGIN > edges.right - edges.left) return null;
      texts.push({
        x: (edges.left + edges.right) / 2,
        y,
        text,
        anchor: 'middle',
        part: region,
      });
    }
  }
  const outside = venn.regions.outside;
  if (outside.length > 0) {
    // One line along the bottom of the box, below the circles, at the right.
    const text = outside.join(', ');
    if (widthOf(text) + 2 * MARGIN > frame.width) return null;
    texts.push({
      x: right - MARGIN - 6,
      y: RADIUS + FRAME.bottom / 2,
      text,
      anchor: 'end',
      part: 'outside',
    });
  }
  const xs = [frame.x, frame.x + frame.width];
  const ys = [frame.y, frame.y + frame.height];
  for (const text of texts) {
    const width = widthOf(text.text);
    const left =
      text.anchor === 'start'
        ? text.x
        : text.anchor === 'end'
          ? text.x - width
          : text.x - width / 2;
    xs.push(left, left + width);
    ys.push(text.y - TEXT_HEIGHT / 2, text.y + TEXT_HEIGHT / 2);
  }
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    radius: RADIUS,
    centres: [-APART, APART],
    frame,
    texts,
    box: {
      x,
      y,
      width: Math.max(...xs) + PAD - x,
      height: Math.max(...ys) + PAD - y,
    },
  };
}
